/**
 * Hook quản lý form gửi yêu cầu liên hệ về một BĐS.
 *
 * Luồng submit:
 * 1. Validate SĐT Việt Nam (bắt buộc).
 * 2. Chuẩn hóa SĐT và gọi contactApi.submitContact.
 * 3. Reset các trường nhạy cảm sau khi gửi thành công.
 */
import { useState } from 'react';
import { TEXT } from '../../../shared/languages';
import { getApiErrorMessage } from '../../../shared/utils/apiErrors';
import { UI_MESSAGES } from '../../../shared/constants/messages';
import {
  getPhoneValidationError,
  normalizeVietnamesePhone,
} from '../../../shared/utils/vietnamesePhone';
import { contactApi } from '../api/contactApi';

/** Khởi tạo form liên hệ với message mặc định từ i18n */
const createInitialContactForm = () => ({
  name: '',
  email: '',
  phone: '',
  message: TEXT.contact.defaultMessage,
});

/**
 * Hook form liên hệ gắn với một propertyId cụ thể.
 * @param {string|number} propertyId - ID BĐS cần liên hệ
 * @returns {Object} State form, thông báo lỗi/thành công và handlers
 */
export const useContactRequestForm = (propertyId) => {
  const [contactForm, setContactForm] = useState(createInitialContactForm);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);

  const updateContactField = (fieldName, fieldValue) => {
    if (fieldName === 'phone') {
      setPhoneError('');
    }

    setContactForm((currentContactForm) => ({
      ...currentContactForm,
      [fieldName]: fieldValue,
    }));
  };

  const submitContactRequest = async () => {
    setIsSubmittingContact(true);
    setErrorMessage('');
    setSuccessMessage('');
    setPhoneError('');

    const phoneValidationKey = getPhoneValidationError(contactForm.phone, { required: true });

    if (phoneValidationKey) {
      setPhoneError(TEXT.validation[phoneValidationKey]);
      setIsSubmittingContact(false);
      return;
    }

    try {
      await contactApi.submitContact({
        ...contactForm,
        phone: normalizeVietnamesePhone(contactForm.phone),
        property_id: propertyId,
      });

      setSuccessMessage(UI_MESSAGES.contactSuccess);
      // Giữ message mặc định, chỉ xóa thông tin cá nhân
      setContactForm((currentContactForm) => ({
        ...currentContactForm,
        name: '',
        email: '',
        phone: '',
      }));
    } catch (requestError) {
      setErrorMessage(getApiErrorMessage(requestError, UI_MESSAGES.contactError));
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return {
    contactForm,
    successMessage,
    errorMessage,
    phoneError,
    isSubmittingContact,
    updateContactField,
    submitContactRequest,
  };
};
